import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaMountain } from 'react-icons/fa';
import { companyInfo } from '../../data/tourData';
import BookingForm from '../../components/common/BookingForm';
import './TourDetail.css';

const PhularaRidge = () => {
  const tourData = {
    name: "Phulara Ridge Trek",
    duration: "04 Nights 05 Days",
    price: "₹ 6,999/-",
    image: "https://res.cloudinary.com/duym2bgp2/image/upload/v1754936217/ehn89o0kdioigrpwf4bi.jpg",
    altitude: "12,000 ft",
    difficulty: "Moderate",
    region: "Uttarakhand",
    highlights: ["Ridge walk", "Meadow views", "Mountain panoramas"],
    bestTime: "May to October",
    description: "The Phulara Ridge Trek is a beautiful moderate trek in Uttarakhand that takes you through scenic ridge walks and offers stunning meadow views. This trek provides panoramic views of the surrounding mountains and is perfect for those looking for a unique ridge walking experience."
  };

  return (
    <>
      <Helmet>
        <title>Phulara Ridge Trek - NomadTrailz</title>
        <meta name="description" content="Experience the beautiful Phulara Ridge Trek in Uttarakhand. Ridge walk trek with stunning meadow views and mountain panoramas." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <section className="tour-hero">
          <div className="hero-background">
            <img
              src={tourData.image}
              alt={tourData.name}
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {tourData.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Ridge walk trek with stunning meadow views and mountain panoramas
            </motion.p>
          </div>
        </section>

        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>{tourData.description}</p>
                  <p>
                    This trek takes you through the beautiful Phulara Ridge, offering stunning 
                    views of the surrounding mountains and valleys. The journey passes through 
                    dense forests, lush meadows, and challenging terrain, making it an exciting 
                    adventure for trekkers of all levels.
                  </p>
                </motion.div>

                <motion.div
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>Ridge walk adventure</li>
                    <li>Stunning meadow views</li>
                    <li>Mountain panoramas</li>
                    <li>Moderate difficulty level</li>
                    <li>Perfect for adventure enthusiasts</li>
                    <li>Photography opportunities</li>
                    <li>High-altitude experience</li>
                    <li>Challenging terrain</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 1: From Dehradun to Sankri</h4>
                    <ul>
                      <li>Depart from Dehradun towards Sankri, a picturesque village in Uttarakhand surrounded by around 250 buildings.</li>
                      <li>Journey takes 8-10 hours, passing through Nainbagh, Naugaon, Purola, Jarmola, Mori Naitwari, and finally Sankri.</li>
                      <li>Sankri serves as the starting point for various hikes including Kedarkantha and Har Ki Dun.</li>
                      <li>Enjoy breathtaking views of the Swargarohini mountain from this little settlement in the lap of the Himalayas.</li>
                      <li>Experience the Zip700 Zipline at Sankri base camp for a unique adventure experience.</li>
                      <li>Check into your accommodation in Sankri for the night, ready for tomorrow's trek.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: From Sankri to Sikolta</h4>
                    <ul>
                      <li>Begin trek from Sankri base camp towards Sikolta, traversing through beautiful pine forest.</li>
                      <li>After 1.5 hours of climbing, arrive at a little bridge that needs to be crossed.</li>
                      <li>Reach "Jainot Thatch" after 30 minutes of hiking, remembering to take cautious steps during ascents.</li>
                      <li>Continue past broken dhabas and climb towards Marikoda, taking in the splendour of Dhunda.</li>
                      <li>Get a sight of the Phulara Ridge nearby from Marikoda.</li>
                      <li>Arrive at "Sikolta," a pleasant meadow section, relax in your tent and have dinner.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: From Sikolta to Bhoj Gadi</h4>
                    <ul>
                      <li>Begin 30-minute ascent through dark and gloomy forest, being cautious of fallen trees.</li>
                      <li>Cross a small descending bridge and then a steep climbing bridge over a lovely stream.</li>
                      <li>Fill water bottles from the stream and continue climbing for 15 minutes to reach Karasu Thatch.</li>
                      <li>Enjoy the yellowish-green flowers at Karasu Thatch and observe the Kedarkantha peak.</li>
                      <li>Continue steady ascent for 30 minutes, passing Rhododendron and Bhoj trees.</li>
                      <li>Enter lush green meadows and reach Bhoj Gadi campsite after 10-15 minutes of hiking.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 4: From Bhoj Gadi to Pushtara</h4>
                    <ul>
                      <li>Embark on one-hour trek to ridge top for dawn views and bird watching.</li>
                      <li>Walk beside mountains offering unique perspective of the landscape.</li>
                      <li>Experience 360-degree panorama of salt-painted mountains from the summit.</li>
                      <li>Enjoy views of snow-covered hills on both sides, creating a magical winter wonderland.</li>
                      <li>After 2.5 hours, reach Pushtara, a serene location to relieve the day's tension.</li>
                      <li>Take in views of Purola valley, pine trees, and several peaks including Hanuman Top, Sarutal Top, and Kedarkantha.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 5: From Pushtara to Sankri to Dehradun</h4>
                    <ul>
                      <li>Begin journey by trekking for 30 minutes to reach Taluka.</li>
                      <li>Pass by destroyed Gujjar huts on the way back to Sankri via Taluka.</li>
                      <li>Traverse from lush grasslands to the middle of captivating pine and oak forest for one hour.</li>
                      <li>Exercise caution while crossing damaged tree trunks that may serve as obstacles.</li>
                      <li>Arrive at the lodge in Sankri after another 40 minutes, marking the end of the Phulara Ridge hike.</li>
                      <li>Travel back to Dehradun from Sankri (8-9 hours journey), bidding farewell to this breathtaking trek.</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div
                  className="things-to-carry-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Things To Carry</h3>
                  <p className="carry-intro">All items listed are essential, unless marked as optional. Items are divided into five categories for your convenience.</p>
                  
                  <div className="carry-categories">
                    <div className="carry-category">
                      <h4><i className="fas fa-hiking"></i> Trekking Gear</h4>
                      <ul>
                        <li>Rucksack with rain cover</li>
                        <li>Day pack bag</li>
                        <li>Head torch with spare batteries</li>
                        <li>UV protection sunglasses</li>
                        <li>Water bottles: Two 1-liter bottles</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-shoe-prints"></i> Footwear</h4>
                      <ul>
                        <li>Non-skid, deep treaded, high-ankle trekking shoes: 1 pair</li>
                        <li>Lightweight slippers or sandals: 1 pair</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-tshirt"></i> Clothing</h4>
                      <ul>
                        <li>Quick-dry warm lower or track pants: 2</li>
                        <li>Full-sleeve T-shirts/sweatshirts: 1 for every 2 days of trekking</li>
                        <li>Thick woolen socks: 1 pair for every 2 days of trekking</li>
                        <li>Thermal body warmer (upper & lower): 1 set</li>
                        <li>Undergarments: 1 for each day of trekking</li>
                        <li>Warm jacket (closed at wrist & neck): 1</li>
                        <li>Full-sleeve sweater: 1</li>
                        <li>Rainwear (jacket & pants): 1 set</li>
                        <li>Waterproof, warm gloves: 1 pair</li>
                        <li>Woolen cap: 1</li>
                        <li>Sun-shielding hat: 1</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-soap"></i> Toiletries</h4>
                      <ul>
                        <li>Personal toiletries kit: Small towel, toilet paper, paper soap, bar soap, toothbrush, toothpaste, cold cream, etc.</li>
                        <li>Sunscreen lotion: Small pack</li>
                        <li>Lip balm: Small pack</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-utensils"></i> Utensils</h4>
                      <ul>
                        <li>Small, lightweight, leak-proof lunch box: 1</li>
                        <li>Plate: 1</li>
                        <li>Spoon: 1</li>
                        <li>Tea/coffee mug (plastic): 1</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-plus-circle"></i> Miscellaneous</h4>
                      <ul>
                        <li className="optional">Camera: (Optional)</li>
                        <li>Personal medicines: Bring plenty if needed; consult your doctor before the trek.</li>
                        <li className="optional">Dry fruits, nuts, chocolate bars (Optional)</li>
                      </ul>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li>Pick and Drop Transfers from/to Dehradun</li>
                    <li>All meals are included with proper hygiene parameters</li>
                    <li>Triple/double sharing tent accommodations are available</li>
                    <li>During the trek, there will be morning and evening tea with a small refreshment</li>
                    <li>All tolls and forest fees</li>
                    <li>Trekking gears (tents, sleeping bags, ropes, gaiters, crampons etc.)</li>
                    <li>First Aid Medical Kit</li>
                    <li>Expert Guide</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li>Food during pick and drop transfers</li>
                    <li>Porters/mules charges</li>
                    <li>Personal expenses</li>
                    <li>Insurance</li>
                    <li>Anything apart from the inclusions</li>
                    <li>GST 5%</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">{tourData.price}</span>
                    <span className="duration">{tourData.duration}</span>
                  </div>

                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>{tourData.region}</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: {tourData.bestTime}</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 8-12 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: {tourData.difficulty}</span>
                    </div>
                    <div className="feature">
                      <FaMountain />
                      <span>Altitude: {tourData.altitude}</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="quick-info-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Quick Info</h3>
                  <div className="info-item">
                    <FaMountain />
                    <div>
                      <strong>Max Altitude:</strong>
                      <span>12,000 feet</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaMapMarkerAlt />
                    <div>
                      <strong>Trek Distance:</strong>
                      <span>25 km (round trip)</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaStar />
                    <div>
                      <strong>Difficulty Level:</strong>
                      <span>Moderate</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaCalendarAlt />
                    <div>
                      <strong>Best Season:</strong>
                      <span>May to October</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="cost-details-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Cost Details</h3>
                  <h4>Cost Per Person (Ex. Dehradun)</h4>
                  <ul>
                    <li><strong>Quad Sharing:</strong> ₹ 6,999 + 5% GST</li>
                    <li><strong>Triple Sharing:</strong> ₹ 7,499 + 5% GST</li>
                    <li><strong>Double Sharing:</strong> ₹ 7,999 + 5% GST</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="pickup-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Pickup Points</h3>
                  <ul>
                    <li><strong>Dehradun:</strong> Natraj chowk between 07:00 AM to 08:00 AM</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="tour-highlights-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Tour Highlights</h3>
                  <ul>
                    <li>Ridge walk adventure</li>
                    <li>Stunning meadow views</li>
                    <li>Mountain panoramas</li>
                    <li>Moderate difficulty level</li>
                    <li>Perfect for adventure enthusiasts</li>
                    <li>Photography opportunities</li>
                    <li>High-altitude experience</li>
                    <li>Challenging terrain</li>
                    <li>Expert guide and support</li>
                  </ul>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName={tourData.name}
                  tourPrice={tourData.price}
                  tourDuration={tourData.duration}
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default PhularaRidge;
